/**
 * BetterBugs — third-party feedback/bug-report widget (screenshot, annotation,
 * screen recording, and a "rewind" of the last few moments of the session).
 * Backs the sidebar's "Feedback" button (`components/organisms/Sidebar.tsx`;
 * identity sync in `SidebarUserRow.tsx`). Ported from
 * trulyinbox-webui's BetterBugs integration, loaded from the vendor CDN rather
 * than as an npm dependency — the same shape as `lib/gleap.ts`.
 *
 * Carve-out from the tokens-only styling rule: the hex colours in
 * `BETTERBUGS_CONFIG` are third-party widget config, rendered inside the SDK's
 * own DOM where the app's CSS variables and Tailwind tokens can't reach.
 * `#fe853a` mirrors `--brand-500` in `styles/tokens.css`.
 */
import { env } from './env';

type BetterbugsInstance = {
  openWidget: () => void;
  closeWidget: () => void;
  destroy: () => void;
  setCaptureRewindOnScreenshot: (enabled: boolean) => void;
  setEmail: (email: string) => void;
  setMetadata: (metadata: Record<string, unknown>) => void;
};

declare global {
  interface Window {
    Betterbugs?: new (config: Record<string, unknown>) => BetterbugsInstance;
    betterBugsInstance?: BetterbugsInstance;
  }
}

const BETTERBUGS_SCRIPT_SRC = 'https://cdn.betterbugs.io/scripts/latest/index.js';
const BETTERBUGS_SCRIPT_ID = 'betterbugs-script';

const BETTERBUGS_CONFIG = {
  styles: { theme: 'light', primaryColor: '#fe853a', primaryTextColor: '#FFFFFF' },
  position: { bottom: '50px', right: '30px' },
  mode: 'production',
  // The widget is opened only from the sidebar Feedback button, never a floating one.
  showActionButton: false,
  recordType: 'recordVideo',
  maxRecordingMinutes: 5,
  mainHeading: 'Submit Feedback',
  emailFieldLabel: 'Your email',
  titleFieldLabel: 'Feedback title',
  descriptionFieldLabel: 'Describe your feedback',
  emailFieldPlaceholder: 'you@company.com',
  titleFieldPlaceholder: 'What is this feedback about?',
  descriptionFieldPlaceholder: "Tell us what worked, what didn't, or what you'd love to see next",
  successMessageHeaderText: 'Thanks for the feedback! 🎉',
  successMessageSubHeaderText: "We've saved it and our team will review shortly.",
} as const;

let loadPromise: Promise<BetterbugsInstance | null> | null = null;
// Bumped by `teardownBetterBugs()` so a script load still in flight at logout
// resolves `null` instead of constructing an instance on `/login`.
let generation = 0;

function createInstance(): BetterbugsInstance | null {
  if (!window.Betterbugs) {
    console.error('[betterbugs] SDK script loaded but window.Betterbugs is undefined');
    return null;
  }
  try {
    const instance = new window.Betterbugs({ apiKey: env.betterbugsApiKey, ...BETTERBUGS_CONFIG });
    // The constructor ignores a `captureRewindOnScreenshot` config key; this
    // setter is what attaches the rewind to screenshot reports.
    instance.setCaptureRewindOnScreenshot(true);
    window.betterBugsInstance = instance;
    return instance;
  } catch (error) {
    console.error('[betterbugs] Failed to initialize widget:', error);
    return null;
  }
}

/**
 * Loads the BetterBugs SDK once and constructs the widget. Resolves `null`
 * (never rejects) when no API key is configured, the constructor is missing or
 * throws, the script fails to load, or a logout tore the session down while
 * the script was still loading. On a load failure the script tag is removed
 * and the memo reset so the next call retries.
 */
export function loadBetterBugs(): Promise<BetterbugsInstance | null> {
  if (loadPromise) return loadPromise;

  if (!env.betterbugsApiKey) {
    loadPromise = Promise.resolve(null);
    return loadPromise;
  }
  if (window.betterBugsInstance) {
    loadPromise = Promise.resolve(window.betterBugsInstance);
    return loadPromise;
  }
  if (window.Betterbugs) {
    loadPromise = Promise.resolve(createInstance());
    return loadPromise;
  }

  const gen = generation;
  loadPromise = new Promise((resolve) => {
    // Reuse a tag left by an earlier module instance (dev HMR) or an earlier
    // generation whose load is still in flight, rather than injecting twice.
    const existing = document.getElementById(BETTERBUGS_SCRIPT_ID);
    let script: HTMLElement;
    if (existing) {
      script = existing;
    } else {
      const created = document.createElement('script');
      created.id = BETTERBUGS_SCRIPT_ID;
      created.async = true;
      created.src = BETTERBUGS_SCRIPT_SRC;
      script = created;
    }
    script.addEventListener('load', () => resolve(gen === generation ? createInstance() : null), {
      once: true,
    });
    script.addEventListener(
      'error',
      () => {
        script.remove();
        if (gen === generation) loadPromise = null;
        console.error('[betterbugs] Failed to load SDK script:', BETTERBUGS_SCRIPT_SRC);
        resolve(null);
      },
      { once: true },
    );
    if (!existing) document.head.appendChild(script);
  });
  return loadPromise;
}

export type BetterBugsIdentity = {
  email: string;
  userId: number | null;
  organizationId: number | null;
  workspaceId: number | null;
};

/**
 * Pre-fills the report's email and attaches tenant metadata. The email goes
 * through `setEmail` on every sync rather than being frozen into the
 * constructor config (as trulyinbox-webui did), so a different user signing
 * in on the same tab replaces it instead of reporting under the previous one.
 */
export function syncBetterBugsIdentity(identity: BetterBugsIdentity): void {
  void loadBetterBugs().then((instance) => {
    if (!instance) return;
    try {
      instance.setEmail(identity.email);
    } catch (error) {
      console.error('[betterbugs] Failed to set email:', error);
    }
    try {
      instance.setMetadata({
        userId: identity.userId,
        organizationId: identity.organizationId,
        workspaceId: identity.workspaceId,
      });
    } catch (error) {
      console.error('[betterbugs] Failed to set metadata:', error);
    }
  });
}

/**
 * Opens the feedback widget, loading the SDK first if needed. Resolves `false`
 * when no instance is available (so the caller can tell the user), `true`
 * otherwise.
 */
export async function openBetterBugsWidget(): Promise<boolean> {
  const instance = await loadBetterBugs();
  if (!instance) return false;
  try {
    instance.openWidget();
  } catch (error) {
    console.error('[betterbugs] Failed to open widget:', error);
  }
  return true;
}

/**
 * Logout teardown. Destroys the SDK session — any captured screenshot or
 * recording, the pre-filled email, the metadata, and the widget's DOM — and
 * drops the cached instance, so the next login rebuilds a fresh one through
 * the `window.Betterbugs` branch of `loadBetterBugs()` without re-injecting
 * the script. `closeWidget()` alone would only hide the widget and leave the
 * previous user's capture and identity live on the tab. Never loads the SDK.
 *
 * SDK limitation: `destroy()` removes the widget DOM but never unmounts its
 * React tree, so its document listeners (including the Cmd/Ctrl+Alt hotkeys)
 * survive, attached to an invisible, session-less widget. A full reload is the
 * only complete teardown.
 */
export function teardownBetterBugs(): void {
  generation += 1;
  loadPromise = null;
  const instance = window.betterBugsInstance;
  delete window.betterBugsInstance;
  try {
    instance?.destroy();
  } catch (error) {
    console.error('[betterbugs] Failed to destroy widget:', error);
  }
}
